import { OptionList } from '@atoms';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useDebounce } from 'src/app/hooks/useDebounce';
import { IUpsellFlow } from 'src/models/UpsellFlow.model';
import { getAllUpsellFlow } from 'src/service/api/upsellFlow';

const UPSELL_FLOW_SELECT_PAGE_SIZE = 30;

const buildFlowOption = (flow: IUpsellFlow) => ({
  label: `${flow.id} - ${flow.name}`,
  value: String(flow.id),
});

export function useOfferUpsellFlowOptions() {
  const [search, setSearch] = useState('');
  const [flows, setFlows] = useState<IUpsellFlow[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const debouncedSearch = useDebounce(search, 500) as string;

  const fetchFlows = useCallback(async (filter: string | number) => {
    const response = await getAllUpsellFlow({
      page: 1,
      limit: UPSELL_FLOW_SELECT_PAGE_SIZE,
      order: '[id,DESC]',
      filter: { filter },
    });

    return response.rows;
  }, []);

  useEffect(() => {
    const normalizedSearch = debouncedSearch.trim();

    if (!normalizedSearch) {
      setFlows([]);
      setIsLoading(false);
      return;
    }

    let isCurrent = true;
    setIsLoading(true);

    fetchFlows(normalizedSearch)
      .then(responseFlows => {
        if (isCurrent) setFlows(responseFlows);
      })
      .catch(() => {
        if (isCurrent) setFlows([]);
      })
      .finally(() => {
        if (isCurrent) setIsLoading(false);
      });

    return () => {
      isCurrent = false;
    };
  }, [debouncedSearch, fetchFlows]);

  const findFlowById = useCallback(
    async (flowId: number) => {
      const responseFlows = await fetchFlows(flowId);
      const matchedFlow =
        responseFlows.find(flow => Number(flow.id) === flowId) ?? null;

      setFlows(matchedFlow ? [matchedFlow] : []);

      return matchedFlow;
    },
    [fetchFlows]
  );

  const options = useMemo<OptionList>(
    () => flows.map(flow => buildFlowOption(flow)),
    [flows]
  );

  return {
    findFlowById,
    isLoading,
    options,
    setSearch,
  };
}
